/**
 * A110Y — engraving zones and detoured temples (generated by tools/engrave/temples.py).
 *
 * Each Handmade model has a side photo of its temple (assets-source/derived/mata-<id>-temple.jpg,
 * linked to the model by silhouette.templePhoto). The tool detours the near temple and crops it with
 * the hinge at the right edge (assets-source/temples/<id>.webp), then finds the straight, flat part
 * near the hinge where the text goes.
 *
 * engrave-zones.json: { "<id>": { x, y, w, h, angle } } in fractions of the cropped image —
 * x = left edge, y = vertical centre, w = length, h = useful thickness, angle in degrees (image
 * coordinates, y down). The hinge is on the right: the zone's hinge side is x + w.
 */
import zonesJson from '../config/engrave-zones.json';
import templesJson from '../config/engrave-temples.json';
import hingesJson from '../config/engrave-hinges.json';

export interface EngraveZone {
  x: number;
  y: number;
  w: number;
  h: number;
  angle: number;
}

export interface TempleInfo {
  model: string;
  colorway: string;
  /** Cropped image size (px). */
  w: number;
  h: number;
  mmPerPx: number;
  /** How the outline was found: photo edges, edges + measured profile, or the profile alone (crystal). */
  mode: string;
  /** Where the zone starts (mm from the hinge): later than 9 mm when a glittering metal core shows near the hinge. */
  zoneStartMm: number;
  zoneMm: number;
  thicknessMm: number;
  stop: string;
}

/**
 * A110Z2 — hinge end of each detoured temple (tools/engrave/hinges.py), for drawing the hinge on the
 * inner view: `end` x of the temple's end, `y` centre line 5 mm before it, `h` thickness there (fractions
 * of the image, hinge on the right), `angle` of the centre line (degrees).
 */
export interface HingeInfo { end: number; y: number; h: number; angle: number }

export const ENGRAVE_ZONES: Record<string, EngraveZone> = zonesJson as Record<string, EngraveZone>;
export const TEMPLE_INFO: Record<string, TempleInfo> = templesJson as Record<string, TempleInfo>;
export const TEMPLE_HINGES: Record<string, HingeInfo> = hingesJson as Record<string, HingeInfo>;

// A110Z3 (David : « utilise les vraies images de branche, c'est pixelisé ») : the real detoured photos, sharpened to
// ≈ 16 px/mm where the product photo was small (tools/engrave/upscale.py, Real-ESRGAN) — same outline and
// proportions as the photos the zones were measured on, so the zones (fractions) still apply.
const urls = import.meta.glob('../../assets-source/temples-hd/*.webp', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const BY_ID: Record<string, string> = {};
for (const [path, url] of Object.entries(urls)) BY_ID[path.slice(path.lastIndexOf('/') + 1, -'.webp'.length)] = url;

/** Zone used for a model without one yet (a future frame before the tool has run): the first half of the temple, upright. */
export const FALLBACK_ZONE: EngraveZone = { x: 0.45, y: 0.3, w: 0.45, h: 0.3, angle: 0 };

export function zoneFor(id: string): EngraveZone {
  return ENGRAVE_ZONES[id] ?? FALLBACK_ZONE;
}

/** URL of the temple photo of a model (hinge on the right), or null. */
export function templeImageUrl(id: string): string | null {
  return BY_ID[id] ?? null;
}

/** Millimetres per pixel of a temple image drawn `width` px wide (the photo's measure; ≈ 147 mm long otherwise). */
export function templeMmPerPx(id: string, width: number): number {
  const info = TEMPLE_INFO[id];
  if (info?.mmPerPx && info.w && width > 0) return (info.mmPerPx * info.w) / width;
  return 147 / Math.max(1, width);
}

/** Zone in image pixels: centre, length, thickness, angle (radians). */
export function zonePx(zone: EngraveZone, w: number, h: number): { cx: number; cy: number; len: number; thick: number; rad: number } {
  return { cx: (zone.x + zone.w / 2) * w, cy: zone.y * h, len: zone.w * w, thick: zone.h * h, rad: (zone.angle * Math.PI) / 180 };
}

/** The same zone on the image mirrored left–right (inner view). */
export function mirrorZone(zone: EngraveZone): EngraveZone {
  return { x: 1 - zone.x - zone.w, y: zone.y, w: zone.w, h: zone.h, angle: -zone.angle };
}

/** Review flags shown on the control page (and kept in mind by the UI). */
export function zoneFlags(info: TempleInfo | undefined): string[] {
  if (!info) return ['photo manquante'];
  const f: string[] = [];
  if (info.mode === 'profil') f.push('contour = profil mesuré (branche cristal)');
  if (info.zoneMm < 25) f.push(`zone courte (${info.zoneMm} mm)`);
  if (/vérifier/.test(info.stop)) f.push('aucune partie plate trouvée');
  if (info.thicknessMm < 4) f.push(`branche fine (${info.thicknessMm} mm)`);
  return f;
}
